import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouting } from '@alycecom/hooks';

import { getGiftHashId, getGiftProduct, getIsGift, getIsGiftInfoLoaded } from '../store/data/data.selectors';
import { getStatuses as getStatusFlags, getIsLoaded } from '../store/statuses/statuses.selectors';
import {
  getActiveStatus,
  getStatuses,
  getVisibleStatuses,
  getProduct,
  getIsDonation,
  getIsConfirmedFlow,
} from '../store/giftFlow/giftFlow.selectors';
import { setActiveStatus, provideStatuses } from '../store/giftFlow/giftFlow.actions';
import { getNextStepUrl, getPreviousStepUrl } from '../helpers/giftStatusSteps.helpers';
import { GIFT_FLOW_CONFIRMATION_ID } from '../../../constants/giftFlow.constants';

export default (useMarketplace = false) => {
  const dispatch = useDispatch();
  const go = useRouting();

  const hashId = useSelector(getGiftHashId);
  const statusFlags = useSelector(getStatusFlags);
  const isStatusFlagsLoaded = useSelector(getIsLoaded);
  const isGiftInfoLoaded = useSelector(getIsGiftInfoLoaded);

  const activeStatus = useSelector(getActiveStatus);
  const statuses = useSelector(getStatuses);
  const visibleStatuses = useSelector(getVisibleStatuses);
  const sentProduct = useSelector(getGiftProduct);
  const flowProduct = useSelector(getProduct);
  const isDonationFlow = useSelector(getIsDonation);
  const isGift = useSelector(getIsGift);
  const isConfirmedFlow = useSelector(getIsConfirmedFlow);

  const handleProvideStatuses = useCallback(() => {
    const isFromMarketplace = flowProduct && flowProduct.fromMarketplace;
    const useMarketplaceStep = useMarketplace || isFromMarketplace;
    dispatch(
      provideStatuses({
        hashId,
        flags: statusFlags,
        flowProduct,
        sentProduct,
        isDonationFlow,
        activeStatus,
        isGift,
        useMarketplaceStep,
      }),
    );
  }, [hashId, statusFlags, useMarketplace, sentProduct, flowProduct, isDonationFlow, activeStatus, isGift, dispatch]);

  const handleSetActiveStatus = useCallback(
    status => {
      dispatch(setActiveStatus(status));
    },
    [dispatch],
  );

  const confirmedFlowStepId = isConfirmedFlow ? GIFT_FLOW_CONFIRMATION_ID : null;
  const nextStepUrl = useMemo(() => getNextStepUrl(statuses, activeStatus, confirmedFlowStepId), [
    statuses,
    activeStatus,
    confirmedFlowStepId,
  ]);
  const previousStepUrl = useMemo(() => getPreviousStepUrl(statuses, activeStatus), [statuses, activeStatus]);

  const goNextStep = useCallback(() => go(nextStepUrl), [go, nextStepUrl]);
  const goPreviousStep = useCallback(() => go(previousStepUrl), [go, previousStepUrl]);

  useEffect(() => {
    if (isStatusFlagsLoaded && isGiftInfoLoaded) {
      handleProvideStatuses();
    }
  }, [isGiftInfoLoaded, isStatusFlagsLoaded, handleProvideStatuses]);

  return {
    activeStatus,
    statuses: visibleStatuses,
    setActiveStatus: handleSetActiveStatus,
    nextStepUrl,
    previousStepUrl,
    goNextStep,
    goPreviousStep,
  };
};
